/**
 * "AI 로 시트 만들기" — 고객이 캐릭터 설명 한 줄로 턴어라운드를 뽑는 곳.
 *
 * 생성은 서버가 Runware(gpt-image-2 low)로 하고, 3D 는 전부 이 브라우저에서 돈다.
 *
 * 잡 흐름: 서버가 정면을 만들고 잡을 연다 → 브라우저가 나머지 일곱 방위를 **병렬로**
 * 요청한다(각 15~25초) → 만든 장은 서버 KV 에 남는다. 탭을 닫아도, 새로고침해도 마지막
 * 잡 id 를 localStorage 에 두었으니 이어받는다. 처음 판처럼 한 요청에 다 실었다가
 * 통째로 잃는 일은 없다.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useForgeStore } from '../state/store.ts';
import { downloadBlob } from '../core/export/glb.ts';

const CODE_KEY = 'texel-forge:credit-code';
const JOB_KEY = 'texel-forge:last-job';

function lsGet(k: string): string { try { return localStorage.getItem(k) ?? ''; } catch { return ''; } }
function lsSet(k: string, v: string) { try { if (v) localStorage.setItem(k, v); else localStorage.removeItem(k); } catch { /* 사생활 모드 */ } }

interface View { azimuth: number; image: string }
interface Job {
  id: string;
  prompt: string;
  azimuths: number[];
  views: View[];
  /** 진행 중인 방위 */
  running: Set<number>;
  /** 실패한 방위 → 메시지 */
  failed: Map<number, string>;
}

async function readJson<T>(res: Response): Promise<T> {
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `서버 오류 ${res.status}`);
  return data;
}

async function toFiles(views: View[]): Promise<{ files: File[]; azimuths: number[] }> {
  const sorted = [...views].sort((a, b) => a.azimuth - b.azimuth);
  const files = await Promise.all(sorted.map(async (v) => {
    const blob = await (await fetch(v.image)).blob();
    return new File([blob], `view-${String(v.azimuth).padStart(3, '0')}.webp`, { type: blob.type || 'image/webp' });
  }));
  return { files, azimuths: sorted.map((v) => v.azimuth) };
}

export function SheetForge() {
  const loadSheetFile = useForgeStore((s) => s.loadSheetFile);
  const loadViewFiles = useForgeStore((s) => s.loadViewFiles);
  const [prompt, setPrompt] = useState('');
  const [code, setCode] = useState(() => lsGet(CODE_KEY));
  const [remaining, setRemaining] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [resumable, setResumable] = useState<{ id: string; prompt: string; count: number; total: number } | null>(null);
  const jobRef = useRef<Job | null>(null);
  const patchJob = useCallback((fn: (j: Job) => Job) => {
    if (!jobRef.current) return;
    jobRef.current = fn(jobRef.current);
    setJob({ ...jobRef.current });
  }, []);

  // 코드를 바꾸면 잔고를 조회한다. 타이핑 중에는 안 두드린다.
  useEffect(() => {
    const trimmed = code.trim();
    if (trimmed.length < 9) { setRemaining(null); return; }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/api/credits?code=${encodeURIComponent(trimmed)}`);
        if (!res.ok) { setRemaining(null); return; }
        const data = (await res.json()) as { remaining: number };
        setRemaining(data.remaining);
        lsSet(CODE_KEY, trimmed);
      } catch { setRemaining(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [code]);

  // 지난 잡이 있으면 이어받기 버튼을 보인다.
  useEffect(() => {
    const id = lsGet(JOB_KEY);
    if (!id) return;
    void (async () => {
      try {
        const data = await readJson<{ jobId: string; prompt: string; azimuths: number[]; views: View[] }>(await fetch(`/api/turnaround/${id}`));
        setResumable({ id: data.jobId, prompt: data.prompt, count: data.views.length, total: data.azimuths.length });
      } catch { lsSet(JOB_KEY, ''); }
    })();
  }, []);

  /** 잡의 빠진 방위들을 병렬로 만든다. 끝나면 있는 뷰로 3D. */
  const fillJob = useCallback(async () => {
    const j = jobRef.current;
    if (!j) return;
    const missing = j.azimuths.filter((a) => a !== 0 && !j.views.some((v) => v.azimuth === a) && !j.running.has(a));
    patchJob((x) => ({ ...x, running: new Set([...x.running, ...missing]), failed: new Map([...x.failed].filter(([a]) => !missing.includes(a))) }));
    await Promise.all(missing.map(async (azimuth) => {
      try {
        const res = await fetch(`/api/turnaround/${j.id}/view`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ azimuth }),
        });
        const data = await readJson<View>(res);
        patchJob((x) => {
          const running = new Set(x.running); running.delete(azimuth);
          return { ...x, running, views: [...x.views.filter((v) => v.azimuth !== azimuth), { azimuth, image: data.image }] };
        });
      } catch (err) {
        patchJob((x) => {
          const running = new Set(x.running); running.delete(azimuth);
          const failed = new Map(x.failed); failed.set(azimuth, err instanceof Error ? err.message : String(err));
          return { ...x, running, failed };
        });
      }
    }));
    // 잔고 갱신(실패 환불 반영)
    const trimmed = code.trim();
    if (trimmed.length >= 9) {
      try {
        const r = await fetch(`/api/credits?code=${encodeURIComponent(trimmed)}`);
        if (r.ok) setRemaining(((await r.json()) as { remaining: number }).remaining);
      } catch { /* 무시 */ }
    }
  }, [code, patchJob]);

  const buildFromJob = useCallback(async () => {
    const j = jobRef.current;
    if (!j || j.views.length < 2) return;
    const { files, azimuths } = await toFiles(j.views);
    await loadViewFiles(files, azimuths);
  }, [loadViewFiles]);

  /** Meshy 식 — 정면을 만들고 그걸 참조로 45° 간격 여덟 뷰. 8크레딧. */
  const generateTurnaround = async () => {
    setBusy(true);
    setError(null);
    setResumable(null);
    try {
      const res = await fetch('/api/turnaround', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ prompt, code: code.trim(), views: 8 }),
      });
      const data = await readJson<{ jobId: string; front: string; azimuths: number[]; remaining: number | string }>(res);
      if (typeof data.remaining === 'number') setRemaining(data.remaining);
      lsSet(JOB_KEY, data.jobId);
      jobRef.current = { id: data.jobId, prompt, azimuths: data.azimuths, views: [{ azimuth: 0, image: data.front }], running: new Set(), failed: new Map() };
      setJob({ ...jobRef.current });
      await fillJob();
      await buildFromJob();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const resume = async () => {
    if (!resumable) return;
    setBusy(true);
    setError(null);
    try {
      const data = await readJson<{ jobId: string; prompt: string; azimuths: number[]; views: View[] }>(await fetch(`/api/turnaround/${resumable.id}`));
      jobRef.current = { id: data.jobId, prompt: data.prompt, azimuths: data.azimuths, views: data.views, running: new Set(), failed: new Map() };
      setJob({ ...jobRef.current });
      setPrompt(data.prompt);
      setResumable(null);
      await fillJob();
      await buildFromJob();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const retryFailed = async () => {
    setBusy(true);
    try { await fillJob(); await buildFromJob(); } finally { setBusy(false); }
  };

  const downloadAll = async () => {
    const j = jobRef.current;
    if (!j) return;
    const { files } = await toFiles(j.views);
    for (const f of files) { downloadBlob(f, f.name); await new Promise((r) => setTimeout(r, 250)); }
  };

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/sheet', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ prompt, code: code.trim() }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error ?? `서버 오류 ${res.status}`);
      }
      const left = res.headers.get('x-credits-remaining');
      if (left && left !== 'unlimited') setRemaining(Number(left));
      const blob = await res.blob();
      await loadSheetFile(new File([blob], `sheet-${Date.now()}.webp`, { type: blob.type || 'image/webp' }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const done = job?.views.length ?? 0;
  const total = job?.azimuths.length ?? 0;
  const failedCount = job?.failed.size ?? 0;
  const runningCount = job?.running.size ?? 0;

  return (
    <div className="section">
      <h2>AI 로 시트 만들기</h2>
      <textarea
        className="prompt"
        rows={3}
        placeholder="캐릭터를 한두 문장으로. 예) 이끼 낀 바위로 된 골렘, 틈에서 주황 용암이 빛남"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        maxLength={600}
      />
      <label className="field" style={{ marginTop: 8 }}>
        <span className="field-head">
          <span className="k">크레딧 코드</span>
          <span className="v">{remaining === null ? '' : `${remaining}장 남음`}</span>
        </span>
        <input
          className="text"
          placeholder="XXXX-XXXX"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          spellCheck={false}
        />
      </label>

      {resumable && !busy && (
        <button className="btn" style={{ width: '100%', marginBottom: 6 }} onClick={() => void resume()}>
          이전 작업 이어받기 — {resumable.count}/{resumable.total}뷰 만들어짐 · “{resumable.prompt.slice(0, 28)}{resumable.prompt.length > 28 ? '…' : ''}”
        </button>
      )}

      <button
        className="btn primary"
        style={{ width: '100%' }}
        disabled={busy || prompt.trim().length < 3}
        onClick={() => void generateTurnaround()}
      >
        {busy
          ? job ? `만드는 중… ${done}/${total}뷰${runningCount ? ` (${runningCount}장 진행)` : ''}` : '정면 만드는 중… (15~25초)'
          : '8뷰 턴어라운드 생성 → 바로 3D (8크레딧)'}
      </button>
      <button
        className="btn"
        style={{ width: '100%', marginTop: 6 }}
        disabled={busy || prompt.trim().length < 3}
        onClick={() => void generate()}
      >
        3뷰 시트 한 장 (1크레딧, 품질 낮음)
      </button>

      {job && (
        <div className="views-strip">
          {job.azimuths.map((a) => {
            const v = job.views.find((x) => x.azimuth === a);
            const state = v ? 'ok' : job.running.has(a) ? 'running' : job.failed.has(a) ? 'failed' : 'pending';
            return (
              <div key={a} className={`view-cell ${state}`} title={job.failed.get(a) ?? `${a}°`}>
                {v ? <img src={v.image} alt={`${a}°`} /> : <span>{state === 'running' ? '…' : state === 'failed' ? '✕' : ''}</span>}
                <em>{a}°</em>
              </div>
            );
          })}
        </div>
      )}
      {job && !busy && (
        <div className="row" style={{ marginTop: 6, gap: 6 }}>
          {failedCount > 0 && (
            <button className="btn" onClick={() => void retryFailed()}>실패 {failedCount}장 재시도 (환불됨·재차감)</button>
          )}
          <button className="btn" onClick={() => void downloadAll()}>이미지 {done}장 저장</button>
        </div>
      )}
      {error && <div className="error-box" style={{ marginTop: 8 }}>{error}</div>}
      <p className="hint">
        정면을 먼저 만들고 그것을 참조 이미지로 45° 간격 일곱 방위를 병렬로 그립니다(gpt-image-2 low, 장당 약
        $0.013). 만든 장은 서버에 48시간 남으니 중간에 끊겨도 위 버튼으로 이어받습니다. 3D 는 이 브라우저 안에서만 돕니다.
      </p>
    </div>
  );
}
